import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseApiState<T> {
  data: T | undefined;
  loading: boolean;
  error: Error | null;
}

export interface UseApiResult<T> extends UseApiState<T> {
  /** Re-runs the fetch function. Returns the latest data. */
  refetch: () => Promise<T | undefined>;
  /** Locally overwrite the data without making a request. */
  setData: (next: T | ((prev: T | undefined) => T)) => void;
}

/**
 * Light-weight React hook for one-shot API calls.
 *
 * Pass any function returning a Promise; deps work like `useEffect`.
 * Re-runs on dep change. Survives strict-mode double-invocation by
 * tracking a per-render "current call" token and ignoring late results.
 *
 * @example
 *   const { data, loading, error, refetch } = useApi(
 *     () => employeeService.all(),
 *     []
 *   );
 */
export function useApi<T>(
  fn: () => Promise<T>,
  deps: ReadonlyArray<unknown> = []
): UseApiResult<T> {
  const [state, setState] = useState<UseApiState<T>>({
    data: undefined,
    loading: true,
    error: null,
  });

  // Hold the latest fn in a ref so the run() identity stays stable across
  // renders even when callers pass a fresh closure on every render.
  const fnRef = useRef(fn);
  fnRef.current = fn;

  // Token to discard stale responses if deps change mid-flight.
  const callIdRef = useRef(0);

  const run = useCallback(async (): Promise<T | undefined> => {
    const myCallId = ++callIdRef.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const result = await fnRef.current();
      if (myCallId !== callIdRef.current) return undefined;
      setState({ data: result, loading: false, error: null });
      return result;
    } catch (err) {
      if (myCallId !== callIdRef.current) return undefined;
      const error = err instanceof Error ? err : new Error(String(err));
      setState((s) => ({ data: s.data, loading: false, error }));
      return undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const setData = useCallback(
    (next: T | ((prev: T | undefined) => T)) =>
      setState((s) => ({
        ...s,
        data: typeof next === 'function' ? (next as (p: T | undefined) => T)(s.data) : next,
      })),
    []
  );

  return { ...state, refetch: run, setData };
}

export default useApi;
